import React, { useEffect, useState } from 'react';
import { Avatar, Tooltip, Typography } from '@mui/material';

import { ChatsSelector } from 'store/chats/selectors';
import { ThunkCloseChat, ThunkGetChats, ThunkReopenChat } from 'store/chats/thunks';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setCurrentChatClosedStatus } from 'store/chats';

import { IChat } from 'services/chats/types';
import { getFormattedDate } from 'utils/dates';

import Button from 'components/Button/Button';

import { ReactComponent as TicketIcon } from 'assets/tickets.icon.svg';

import useStyles from '../styles';
import { LOCAL_STORAGE_PSA } from '../../../constants/storage';
import { PsaTypes } from '../../../services/config/types';
import { tt } from '@i18n/tt';
import { PortalConfigSelector } from 'store/config/selectors';
import EllipsisTooltip from '../../../components/EllipsisTooltip';

interface IProps {
  activeChat: number;
}

const ChatHeader: React.FC<IProps> = ({ activeChat }) => {
  const isAutoTask = localStorage.getItem(LOCAL_STORAGE_PSA) === PsaTypes[PsaTypes.Autotask];

  const [processing, setProcessing] = useState(false);
  const [selectedChat, setSelectedChat] = useState<IChat | null>(null);

  const classes = useStyles();
  const chats = useAppSelector(ChatsSelector);
  const { allowReOpenChat } = useAppSelector(PortalConfigSelector);
  const dispatch = useAppDispatch();

  const handleResolve = (): void => {
    setProcessing(true);
    dispatch(ThunkCloseChat({ chatId: activeChat }))
      .unwrap()
      .then(() => {
        dispatch(setCurrentChatClosedStatus({ chatId: activeChat, status: true }));
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  const handleReopen = (): void => {
    setProcessing(true);
    dispatch(ThunkReopenChat({ chatId: activeChat }))
      .unwrap()
      .then(() => {
        dispatch(setCurrentChatClosedStatus({ chatId: activeChat, status: false }));
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  useEffect(() => {
    const chat = chats.find(chat => +chat?.id === +activeChat);
    if (chat) {
      setSelectedChat(chat);
    }
  }, [activeChat, chats]);

  return (
    <div className={classes.chatHeader}>
      <div className={classes.chatHeaderActions}>
        <div className={classes.chatHeaderInfo}>
          <Avatar src={selectedChat?.techProfileImg} />
          <Typography variant="body1" noWrap>
            <b>{selectedChat?.techName}</b>
          </Typography>
          <div className={classes.messageDate}>{`${tt('common.createdOn')} ${getFormattedDate(
            selectedChat?.createdOn ?? '',
          )}`}</div>

          <div className={classes.divider} />

          <Typography variant="body1" className={classes.ticketStatus}>
            <TicketIcon className={classes.ticket} />
            {`#${
              isAutoTask
                ? selectedChat?.ticketNumber ?? selectedChat?.ticketId
                : selectedChat?.ticketId
            }`}
          </Typography>

          <div className={classes.divider} />

          <div className={classes.chatStatus}>{selectedChat?.ticketStatus}</div>
        </div>

        {!selectedChat?.isClosed ? (
          <Button
            text={tt('pages.chats.resolveButton')}
            disabled={processing}
            onClick={handleResolve}
          />
        ) : (
          allowReOpenChat && (
            <Button
              text={tt('pages.chats.reopenButton')}
              disabled={processing}
              onClick={handleReopen}
            />
          )
        )}
      </div>

      <EllipsisTooltip
        text={selectedChat?.name ?? ''}
        className={classes.chatNameText}
      />
    </div>
  );
};

export default ChatHeader;
